import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { SERP_PLATFORMS } from "../../../config";
import { useDispatch, useSelector } from "react-redux";
import { updateSelectedPlatform, resetSelectedPlatform } from "./platformSlice";
import "./platform.scss";
import {
  checkReviewerPermissions,
  checkClassTestPermissions,
} from "../../util/userUtil";
import { useLocation, useHistory } from "react-router-dom";
import ReactGA from "react-ga4";
import {
  isSelectedShowApplicable,
  isSelectedSortApplicable,
  parsePlatformsFromUrl,
} from "../../util/platformUtil";
import { resetSelectedShow } from "../show/showSlice";
import { resetSelectedSort } from "../sort/sortSlice";
import { updateSendSearchRequest } from "../search/searchSlice";
import { resetDisciplineCount } from "../subject/subjectSlice";
import {
  updateIsRedirectToSearch,
  updateCurrentResultPage,
  reinitializedSearchResult,
} from "../searchResult/searchResultSlice";
import useRefPlatforms from "../../util/useRefPlatforms";
import _ from "lodash";

export function Platform() {
  const dispatch = useDispatch();
  const selectedPlatform = useSelector(
    (state) => state.platform.selectedPlatform
  );
  const selectedShow = useSelector((state) => state.show.selectedShow);
  const selectedSort = useSelector((state) => state.sort.selectedSort);
  const userData = useSelector((state) => state.user.userData);
  const sendSearchRequest = useSelector(
    (state) => state.search.sendSearchRequest
  );
  const isRedirectToSearch = useSelector(
    (state) => state.searchResult.isRedirectToSearch
  );
  const isPlatformClearedByUser = useSelector(
    (state) => state.platform.isPlatformClearedByUser
  );
  const [handleRefPlatform] = useRefPlatforms();
  const [platforms, setPlatforms] = useState([]);
  const [isPlatformModifiable, setIsPlatformModifiable] = useState(true);
  let location = useLocation();
  let history = useHistory();
  let queryParams = new URLSearchParams(location.search);
  const hasIntegrationToken = queryParams.has("integration_context_token");
  const platformsParam = queryParams.get("platforms");
  const selectedPlatformsParam = queryParams.get("selectedPlatforms");
  const modifyPlatformFilterParam = queryParams.get("modifyPlatformFilter");
  const checkRevelConsolePermission = () => {
    return _.some(userData?.permissions, (permission) => 
      permission === "create_reveltest_course"
    );
  };

  useEffect(() => {
    setPlatforms((prevState) => []);
    
    // Handle modifyPlatformFilter parameter for LMS flow
    // Only gray out when modifyPlatformFilter=false, all other times (including missing) act like true
    if (hasIntegrationToken) {
      if (modifyPlatformFilterParam !== null && modifyPlatformFilterParam.toLowerCase() === "false") {
        setIsPlatformModifiable(false);
      } else {
        // Default to true when modifyPlatformFilter is missing or any value other than "false"
        setIsPlatformModifiable(true);
      }
    } else {
      setIsPlatformModifiable(true);
    }

    // Check if we should use LMS flow (has token, has platforms param, and has valid platforms)
    let useLMSFlow = false;
    let allowedPlatforms = [];
    
    if (hasIntegrationToken && platformsParam) {
      // Parse platforms from URL
      const urlPlatforms = parsePlatformsFromUrl(platformsParam);
      
      // Filter out COLLECTION, CUSTOM, MLM, and XL_PLUS (always excluded in LMS flow)
      // ETEXT is NOT filtered here - if explicitly passed in URL, it should be shown
      allowedPlatforms = urlPlatforms.filter(
        (platform) =>
          platform.type !== SERP_PLATFORMS.COLLECTION.type &&
          platform.type !== SERP_PLATFORMS.CUSTOM.type &&
          platform.type !== SERP_PLATFORMS.MLM.type &&
          platform.type !== SERP_PLATFORMS.XL_PLUS.type
      );

      // Only use LMS flow if we have at least one valid platform after filtering
      useLMSFlow = allowedPlatforms.length > 0;
    }

    // LMS Flow: When integration_context_token AND platforms parameter are present AND has valid platforms
    if (useLMSFlow) {
        // Build platforms array synchronously
        const platformsToSet = [...allowedPlatforms];

        // Add conditionally added platforms (CLASS_TEST and REVIEWER) if user has permissions
        if (
          userData &&
          userData.isInstructor &&
          userData.permissions &&
          userData.permissions.length > 0 &&
          (checkClassTestPermissions(userData.permissions) ||
            checkRevelConsolePermission())
        ) {
          platformsToSet.push(SERP_PLATFORMS.CLASS_TEST);
        }

        if (
          userData &&
          userData.isInstructor &&
          userData.permissions &&
          userData.permissions.length > 0 &&
          checkReviewerPermissions(userData.permissions)
        ) {
          platformsToSet.push(SERP_PLATFORMS.REVIEWER);
        }

        // Set all platforms at once
        setPlatforms(platformsToSet);

        // Handle platform selection immediately after setting platforms
        if (platformsToSet.length > 0) {
          let platformToSelect = null;

          // If selectedPlatforms parameter is provided, sync from URL
          if (selectedPlatformsParam) {
            const mappedSelectedPlatform = parsePlatformsFromUrl(selectedPlatformsParam);
            
            if (mappedSelectedPlatform.length > 0) {
              const selectedPlatformObj = mappedSelectedPlatform[0];
              
              // Check if the selected platform is in the allowed platforms list
              const isPlatformAllowed = platformsToSet.some(
                (platform) => platform.type === selectedPlatformObj.type
              );
              
              if (isPlatformAllowed) {
                platformToSelect = selectedPlatformObj;
              } else {
                // Fallback to first platform in platforms list
                platformToSelect = platformsToSet[0];
              }
            } else {
              // Invalid selectedPlatforms value, fallback to first platform
              platformToSelect = platformsToSet[0];
            }
          } else {
            // If selectedPlatforms parameter is missing, DON'T auto-select
            // User either hasn't selected yet or explicitly cleared the filter
            platformToSelect = null;
          }

          // Handle platform selection:
          // Only sync from URL when selectedPlatformsParam is explicitly provided
          // AND the user hasn't explicitly cleared the filter
          if (platformToSelect && selectedPlatformsParam && !isPlatformClearedByUser) {
            // selectedPlatforms parameter syncs from URL - always sync when param is present
            if (selectedPlatform !== platformToSelect.type) {
              dispatch(
                updateSelectedPlatform({
                  type: platformToSelect.type,
                  displayValue: platformToSelect.displayValue,
                })
              );
            }
          }
          // When selectedPlatformsParam is missing or user cleared the filter,
          // don't auto-select anything - allows user to see "All" results
        } else {
          // No platforms available, reset selection
          if (selectedPlatform) {
            dispatch(resetSelectedPlatform());
          }
        }
    } else {
      // Normal Flow: When platforms parameter is missing OR all values are invalid
      // Normal Flow: Existing logic (unchanged)
      if (
        (window.piSession &&
          window.piSession.userId() &&
          userData &&
          userData.id &&
          !isRedirectToSearch) ||
        (window.piSession &&
          !window.piSession.userId() &&
          !isRedirectToSearch &&
          !hasIntegrationToken)
      ) {
        handleRefPlatform();
        dispatch(updateIsRedirectToSearch(true));
        dispatch(updateSendSearchRequest(sendSearchRequest + 1));
      }

      if (
        queryParams.has("ref") &&
        queryParams.get("ref").toLowerCase().includes("revel")
      ) {
        setPlatforms((prevState) => [...prevState, SERP_PLATFORMS.REVEL]);
      } else {
        // Exclude ETEXT in LMS flow (when integration_context_token is present)
        if (!hasIntegrationToken) {
          setPlatforms((prevState) => [...prevState, SERP_PLATFORMS.ETEXT]);
        }
        setPlatforms((prevState) => [...prevState, SERP_PLATFORMS.REVEL]);
        if (
          userData &&
          userData.isInstructor &&
          userData.organizations &&
          userData.organizations.length > 0 &&
          !hasIntegrationToken
        ) {
          setPlatforms((prevState) => [...prevState, SERP_PLATFORMS.COLLECTION]);
        }
        setPlatforms((prevState) => [...prevState, SERP_PLATFORMS.APPLICATION]);
        if (!hasIntegrationToken) {
          if (
            queryParams.has("ref") &&
            queryParams.get("ref").toLowerCase() === "console"
          ) {
            //Do Nothing here
          } else {
            setPlatforms((prevState) => [...prevState, SERP_PLATFORMS.CUSTOM]);
          }
        }
      }
      
      // Add conditionally added platforms for normal flow too
      if (
        userData &&
        userData.isInstructor &&
        userData.permissions &&
        userData.permissions.length > 0 &&
        (checkClassTestPermissions(userData.permissions) ||
          checkRevelConsolePermission())
      ) {
        setPlatforms((prevState) => [...prevState, SERP_PLATFORMS.CLASS_TEST]);
      }

      if (
        userData &&
        userData.isInstructor &&
        userData.permissions &&
        userData.permissions.length > 0 &&
        checkReviewerPermissions(userData.permissions)
      ) {
        setPlatforms((prevState) => [...prevState, SERP_PLATFORMS.REVIEWER]);
      }
    }
  }, [userData, hasIntegrationToken, platformsParam, selectedPlatformsParam, modifyPlatformFilterParam, dispatch, location, isPlatformClearedByUser]);

  // Auto-select REVEL in fallback flow (has integration_token but no platforms param)
  useEffect(() => {
    // Only auto-select if:
    // 1. Has integration token
    // 2. No platforms parameter (fallback flow) - empty string counts as "no platforms param"
    // 3. Platforms array includes REVEL
    // 4. Either no platform is currently selected OR the selected platform is not in the current platforms list
    // 5. User hasn't explicitly cleared the platform filter
    if (
      hasIntegrationToken &&
      !platformsParam &&
      platforms.length > 0 &&
      platforms.some((p) => p.type === SERP_PLATFORMS.REVEL.type) &&
      !isPlatformClearedByUser
    ) {
      // Check if currently selected platform is valid for the current platforms list
      const isSelectedPlatformValid = selectedPlatform && 
        platforms.some((p) => p.type === selectedPlatform);
      
      // If no platform is selected OR the selected platform is not valid for current flow, select REVEL
      if (!selectedPlatform || !isSelectedPlatformValid) {
        // Find REVEL platform from the platforms array
        const revelPlatform = platforms.find(
          (p) => p.type === SERP_PLATFORMS.REVEL.type
        );
        if (revelPlatform) {
          dispatch(
            updateSelectedPlatform({
              type: revelPlatform.type,
              displayValue: revelPlatform.displayValue,
            })
          );
        }
      }
    }
  }, [platforms, hasIntegrationToken, platformsParam, selectedPlatform, dispatch, isPlatformClearedByUser]);

  const platformClickHandler = (event) => {
    // Prevent platform change if modifyPlatformFilter is false
    if (!isPlatformModifiable) {
      return;
    }
    
    let hasInstitution = false;
    // Clear search results immediately when platform changes to prevent showing stale results
    dispatch(reinitializedSearchResult());
    dispatch(updateCurrentResultPage(1));
    dispatch(resetDisciplineCount());
    if (
      userData &&
      userData.isInstructor &&
      userData.organizations &&
      userData.organizations.length > 0
    ) {
      hasInstitution = true;
    }
    const tempShowApplicable = isSelectedShowApplicable(
      event.target.value,
      selectedShow,
      hasInstitution,
      hasIntegrationToken
    );
    if (!tempShowApplicable) {
      dispatch(resetSelectedShow());
    }
    const tempSortApplicable = isSelectedSortApplicable(
      event.target.value,
      selectedSort
    );
    if (!tempSortApplicable) {
      dispatch(resetSelectedSort());
    }
    const newPlatformDisplayValue = event.target.getAttribute("data-displayvalue");
    dispatch(
      updateSelectedPlatform({
        type: event.target.value,
        displayValue: newPlatformDisplayValue,
      })
    );
    
    // Update URL's selectedPlatforms parameter if we're in LMS flow
    if (hasIntegrationToken && platformsParam) {
      const updatedQueryParams = new URLSearchParams(location.search);
      updatedQueryParams.set("selectedPlatforms", newPlatformDisplayValue);
      history.push({
        pathname: location.pathname,
        search: updatedQueryParams.toString(),
      });
    }
    
    dispatch(updateSendSearchRequest(sendSearchRequest + 1));
    ReactGA.event({
      category: "Facet",
      action: "click items on SERP filter by Platform",
      label: event.target.value,
    });
  };

  const validatePlatformByChecked = (type) => {
    return selectedPlatform === type;
  };

  return (
    <Form>
      <div key={`custom-inline-radio`} className="platform" role="radiogroup">
        {platforms &&
          platforms.map((platform, index) => (
            <Form.Check
              type="radio"
              id={`rd-${index}`}
              key={index}
              role="radio"
              aria-checked={validatePlatformByChecked(platform.type)}
            >
              <Form.Check.Input
                type="radio"
                isValid
                className="input-platform"
                name="grpPlatforms"
                value={platform.type}
                data-displayvalue={platform.displayValue}
                checked={validatePlatformByChecked(platform.type)}
                onChange={(event) => platformClickHandler(event)}
                disabled={!isPlatformModifiable}
              />
              <Form.Check.Label>
                {platform.displayValue === "REVEL" ? (
                  <span className="platform-facet-txt">
                    {platform.displayValue} <sup> TM </sup>
                  </span>
                ) : (
                  <span className="platform-facet-txt">
                    {platform.displayValue}
                  </span>
                )}
              </Form.Check.Label>
            </Form.Check>
          ))}
      </div>
    </Form>
  );
}
